import { EmissionStatus, PsaProjectEmission } from 'src/app/shared/interfaces/psa-project-emission';

const NESTED_EMISSION_KEYS = [
  'carbonEmission',
  'carbonCredit',
  'carbonCredits',
  'issuance',
  'emission',
] as const;

function isEmissionLike(value: unknown): value is PsaProjectEmission {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as PsaProjectEmission;
  return candidate.id != null
    || !!candidate.serialCode
    || !!candidate.emissionNumber
    || Array.isArray(candidate.harvests);
}

export function extractEmissionFromApiResponse(response: unknown): PsaProjectEmission | undefined {
  if (!response || typeof response !== 'object') {
    return undefined;
  }

  const payload = response as { data?: unknown };
  const data = payload.data;

  if (isEmissionLike(data)) {
    return data;
  }

  if (data && typeof data === 'object') {
    for (const key of NESTED_EMISSION_KEYS) {
      const nested = (data as Record<string, unknown>)[key];
      if (isEmissionLike(nested)) {
        return nested;
      }
    }
  }

  return isEmissionLike(response) ? response as PsaProjectEmission : undefined;
}

export interface EmissionStepTag {
  label: string;
  tone: 'success' | 'danger' | 'neutral' | 'warning' | 'info';
  icon: string;
}

export type EmissionDrawerFooterMode = 'understand' | 'audit' | 'developer' | 'clone';

const STEP_LABELS = {
  emission: 'Emissão do crédito enviada',
  audit: 'Pendente de verificação do auditor',
  auditRejected: 'Rejeitado pelo Auditor',
  report: 'Aguardando recebimento do relatório',
  registered: 'Emissão e Registro realizados na B3',
} as const;

function successTag(label: string): EmissionStepTag {
  return { label, tone: 'success', icon: 'check_circle' };
}

function auditInProgressTag(label: string): EmissionStepTag {
  return { label, tone: 'warning', icon: 'autorenew' };
}

function reportInProgressTag(label: string): EmissionStepTag {
  return { label, tone: 'info', icon: 'autorenew' };
}

function dangerTag(label: string): EmissionStepTag {
  return { label, tone: 'danger', icon: 'do_not_disturb_on' };
}

function pendingTag(label: string): EmissionStepTag {
  return { label, tone: 'neutral', icon: 'check_circle' };
}

const STATUS_CODE_TO_LABEL: Record<string, EmissionStatus> = {
  PENDING_AUDITOR_VERIFICATION: EmissionStatus.PENDING_AUDITOR_VERIFICATION,
  APPROVED_BY_AUDITOR: EmissionStatus.APPROVED_BY_AUDITOR,
  REJECTED_BY_AUDITOR: EmissionStatus.REJECTED_BY_AUDITOR,
  ADJUSTED_BY_DEVELOPER: EmissionStatus.ADJUSTED_BY_DEVELOPER,
  PENDING_DEVELOPER_CONFIRMATION: EmissionStatus.PENDING_DEVELOPER_CONFIRMATION,
  CONFIRMED_BY_DEVELOPER: EmissionStatus.CONFIRMED_BY_DEVELOPER,
  REJECTED_BY_DEVELOPER: EmissionStatus.REJECTED_BY_DEVELOPER,
  REGISTERED_IN_B3: EmissionStatus.REGISTERED_IN_B3,
};

export function normalizeEmissionDisplayStatus(status?: string): string | undefined {
  if (!status?.trim()) {
    return status;
  }

  return STATUS_CODE_TO_LABEL[status] ?? status;
}

export function resolveEmissionStepTags(status?: string): EmissionStepTag[] {
  const normalizedStatus = normalizeEmissionDisplayStatus(status);

  switch (normalizedStatus) {
    case EmissionStatus.PENDING_AUDITOR_VERIFICATION:
    case EmissionStatus.ADJUSTED_BY_DEVELOPER:
      return [
        successTag(STEP_LABELS.emission),
        auditInProgressTag(STEP_LABELS.audit),
        pendingTag(STEP_LABELS.report),
        pendingTag(STEP_LABELS.registered),
      ];
    case EmissionStatus.REJECTED_BY_AUDITOR:
      return [
        successTag(STEP_LABELS.emission),
        dangerTag(STEP_LABELS.auditRejected),
        pendingTag(STEP_LABELS.report),
        pendingTag(STEP_LABELS.registered),
      ];
    case EmissionStatus.APPROVED_BY_AUDITOR:
    case EmissionStatus.PENDING_DEVELOPER_CONFIRMATION:
      return [
        successTag(STEP_LABELS.emission),
        successTag(STEP_LABELS.audit),
        reportInProgressTag(STEP_LABELS.report),
        pendingTag(STEP_LABELS.registered),
      ];
    case EmissionStatus.REJECTED_BY_DEVELOPER:
      return [
        successTag(STEP_LABELS.emission),
        successTag(STEP_LABELS.audit),
        dangerTag(STEP_LABELS.report),
        pendingTag(STEP_LABELS.registered),
      ];
    case EmissionStatus.CONFIRMED_BY_DEVELOPER:
    case EmissionStatus.REGISTERED_IN_B3:
      return [
        successTag(STEP_LABELS.emission),
        successTag(STEP_LABELS.audit),
        successTag(STEP_LABELS.report),
        successTag(STEP_LABELS.registered),
      ];
    default:
      return [
        successTag(STEP_LABELS.emission),
        pendingTag(STEP_LABELS.audit),
        pendingTag(STEP_LABELS.report),
        pendingTag(STEP_LABELS.registered),
      ];
  }
}

export function shouldShowRejectionReason(status?: string): boolean {
  const normalizedStatus = normalizeEmissionDisplayStatus(status);
  return normalizedStatus === EmissionStatus.REJECTED_BY_AUDITOR
    || normalizedStatus === EmissionStatus.REJECTED_BY_DEVELOPER;
}

export interface EmissionDrawerPermissions {
  hasAudit: boolean;
  hasRegister: boolean;
}

export function resolveEmissionFooterMode(
  status: string | undefined,
  permissions: EmissionDrawerPermissions,
): EmissionDrawerFooterMode {
  const normalizedStatus = normalizeEmissionDisplayStatus(status);

  if (permissions.hasAudit && normalizedStatus === EmissionStatus.PENDING_AUDITOR_VERIFICATION) {
    return 'audit';
  }

  if (permissions.hasRegister) {
    if (normalizedStatus === EmissionStatus.PENDING_DEVELOPER_CONFIRMATION) {
      return 'developer';
    }

    if (normalizedStatus === EmissionStatus.REJECTED_BY_AUDITOR) {
      return 'clone';
    }
  }

  return 'understand';
}
